import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IoCheckmark } from 'react-icons/io5';
import { Fonts, Palette } from '../../components/utils';
import { createChatDirect } from '../../redux/actions/chat';
import { setToast } from '../../redux/actions/window';
import { requestPending } from '../../utils/status';
import { CREATE_CHAT_DIRECT } from '../../redux/constants';
import { chatErrorMessage } from '../../helper/chat';
import { UserRole, UserType } from '../users/constant';
import LoadingAvatar from '../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../utils/colorCode';
import { baseURL as searchBaseURL } from '../../utils/axios-new';
import useDebouncedSearch from './useDebouncedSearch';
import ChatPanelHeader from './ChatPanelHeader';
import ChatBuildingSelect from './ChatBuildingSelect';
import ChatRadioSelect, {
  LoadMoreButton,
  MenuEmpty,
  Option,
  OptionName,
  RadioMark
} from './ChatRadioSelect';
import ChatSearchField from './ChatSearchField';
import { ChatEmptyText } from './chatSharedStyles';

const resolveDefaultBuildingId = (buildings, initialBuildingId) => {
  const list = Array.isArray(buildings) ? buildings : [];
  const initial = Number(initialBuildingId) || 0;
  if (initial > 0 && list.some((b) => Number(b.id) === initial)) {
    return initial;
  }
  return Number(list[0]?.id) || 0;
};

const isResidentUser = (user) => {
  const roleId = Number(user.role_id);
  const typeId = Number(user.type_id);
  if (roleId === UserRole.USER) return true;
  return typeId === UserType.RESIDENT || typeId === UserType.CORESIDENT;
};

const normalizeResident = (user) => {
  const fullName = String(user.name || '').trim();
  const nameParts = fullName ? fullName.split(/\s+/) : [];
  return {
    id: user.id,
    first_name: user.first_name || nameParts[0] || '',
    last_name: user.last_name || nameParts.slice(1).join(' ') || '',
    unit_num: user.unit_num || '',
    avatar: user.avatar || '',
    role_id: user.role_id,
    type_id: user.type_id
  };
};

const residentDisplayName = (user) =>
  `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
  (user?.id ? `User ${user.id}` : '');

const RESIDENTS_PER_PAGE = 20;

const mergeResidents = (existing, incoming) => {
  const seen = new Set(existing.map((user) => Number(user.id)));
  const next = [...existing];
  incoming.forEach((user) => {
    const id = Number(user.id);
    if (!seen.has(id)) {
      seen.add(id);
      next.push(user);
    }
  });
  return next;
};

const fetchResidents = async ({ buildingId = 0, orgId = 0, query = '', page = 1, signal }) => {
  if (orgId <= 0) {
    throw new Error('Failed to load residents');
  }

  const response = await fetch(`${searchBaseURL}/search2?entity=user&org=${orgId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('idToken')}`,
      'Content-Type': 'application/json',
      'org-location': window.localStorage.data_center || ''
    },
    body: JSON.stringify({
      search: query || '',
      building_id: buildingId,
      category: -1,
      filters: {},
      ordering: {},
      pagination: {
        page,
        limit: RESIDENTS_PER_PAGE
      }
    }),
    signal
  });

  if (!response.ok) {
    throw new Error('Failed to load residents');
  }

  const json = await response.json();
  const list = Array.isArray(json?.data) ? json.data : [];
  const currentPage = Number(json?.pagination?.currentPage) || page;
  const lastPage = Math.max(Number(json?.pagination?.totalPages) || currentPage, currentPage);

  return {
    list: list.filter(isResidentUser).map(normalizeResident),
    page: currentPage,
    lastPage
  };
};

const ChatNewConversation = ({ buildings = [], initialBuildingId = 0, onBack, onClose, onStarted }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalOrgId = useSelector((state) => state.Organization?.globalOrgId);
  const authOrgId = useSelector((state) => state.auth?.orgId);
  const orgId =
    Number(globalOrgId) > 0
      ? Number(globalOrgId)
      : Number(authOrgId) > 0
        ? Number(authOrgId)
        : 0;

  const [selectedResident, setSelectedResident] = useState(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState(() =>
    resolveDefaultBuildingId(buildings, initialBuildingId)
  );
  const [buildingResidents, setBuildingResidents] = useState([]);
  const [buildingPage, setBuildingPage] = useState(1);
  const [buildingLastPage, setBuildingLastPage] = useState(1);
  const [loadingBuildingResidents, setLoadingBuildingResidents] = useState(false);
  const [loadingMoreBuilding, setLoadingMoreBuilding] = useState(false);
  const [buildingResidentsFailed, setBuildingResidentsFailed] = useState(false);

  const [residentSearch, setResidentSearch] = useState('');
  const [debouncedResidentSearch, setDebouncedResidentSearch] = useDebouncedSearch(residentSearch);
  const [searchResidents, setSearchResidents] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLastPage, setSearchLastPage] = useState(1);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingMoreSearch, setLoadingMoreSearch] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  const { createDirectStatus, createDirectError } = useSelector((state) => state.chat);

  useEffect(() => {
    setSelectedBuildingId(resolveDefaultBuildingId(buildings, initialBuildingId));
    setSelectedResident(null);
    setResidentSearch('');
    setDebouncedResidentSearch('');
    setSearchResidents([]);
    setSearchPage(1);
    setSearchLastPage(1);
  }, [buildings, initialBuildingId, setDebouncedResidentSearch]);

  useEffect(() => {
    if (!selectedBuildingId) {
      setBuildingResidents([]);
      setBuildingPage(1);
      setBuildingLastPage(1);
      return undefined;
    }

    const controller = new AbortController();
    setLoadingBuildingResidents(true);
    setBuildingResidentsFailed(false);
    setSelectedResident(null);
    setBuildingPage(1);
    setBuildingLastPage(1);

    fetchResidents({
      buildingId: selectedBuildingId,
      orgId,
      query: '',
      page: 1,
      signal: controller.signal
    })
      .then(({ list, page, lastPage }) => {
        setBuildingResidents(list);
        setBuildingPage(page);
        setBuildingLastPage(lastPage);
        setLoadingBuildingResidents(false);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setBuildingResidents([]);
        setBuildingResidentsFailed(true);
        setLoadingBuildingResidents(false);
      });

    return () => controller.abort();
  }, [selectedBuildingId, orgId]);

  useEffect(() => {
    const query = debouncedResidentSearch.trim();
    if (!query || !selectedBuildingId) {
      setSearchResidents([]);
      setSearchPage(1);
      setSearchLastPage(1);
      setLoadingSearch(false);
      setSearchFailed(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoadingSearch(true);
    setSearchFailed(false);
    setSearchPage(1);
    setSearchLastPage(1);

    fetchResidents({
      buildingId: selectedBuildingId,
      orgId,
      query,
      page: 1,
      signal: controller.signal
    })
      .then(({ list, page, lastPage }) => {
        setSearchResidents(list);
        setSearchPage(page);
        setSearchLastPage(lastPage);
        setLoadingSearch(false);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setSearchResidents([]);
        setSearchFailed(true);
        setLoadingSearch(false);
      });

    return () => controller.abort();
  }, [debouncedResidentSearch, selectedBuildingId, orgId]);

  const selectedBuildingName =
    buildings.find((b) => Number(b.id) === Number(selectedBuildingId))?.name || t('Building');

  const starting = createDirectStatus === requestPending(CREATE_CHAT_DIRECT);
  const hasBuildings = buildings.length > 0;
  const selectedResidentId = selectedResident?.id || null;
  const canStart = Boolean(selectedResidentId) && !starting;
  const selectedLabel = useMemo(
    () => (selectedResident ? residentDisplayName(selectedResident) : ''),
    [selectedResident]
  );
  const showingSearchResults = Boolean(debouncedResidentSearch.trim());
  const hasMoreBuilding = buildingPage < buildingLastPage;
  const hasMoreSearch = searchPage < searchLastPage;

  const handleBuildingChange = (e) => {
    setSelectedBuildingId(Number(e.target.value) || 0);
  };

  const handleSelectResident = (user) => {
    setSelectedResident(user);
    setResidentSearch('');
    setDebouncedResidentSearch('');
    setSearchResidents([]);
    setSearchPage(1);
    setSearchLastPage(1);
    setSearchFailed(false);
  };

  const handleLoadMoreBuilding = async () => {
    if (!selectedBuildingId || loadingMoreBuilding || !hasMoreBuilding) return;
    setLoadingMoreBuilding(true);
    try {
      const nextPage = buildingPage + 1;
      const { list, page, lastPage } = await fetchResidents({
        buildingId: selectedBuildingId,
        orgId,
        query: '',
        page: nextPage
      });
      setBuildingResidents((prev) => mergeResidents(prev, list));
      setBuildingPage(page);
      setBuildingLastPage(lastPage);
    } catch (err) {
      // Keep current list; user can retry Load more.
    } finally {
      setLoadingMoreBuilding(false);
    }
  };

  const handleLoadMoreSearch = async () => {
    const query = debouncedResidentSearch.trim();
    if (!query || !selectedBuildingId || loadingMoreSearch || !hasMoreSearch) return;
    setLoadingMoreSearch(true);
    try {
      const nextPage = searchPage + 1;
      const { list, page, lastPage } = await fetchResidents({
        buildingId: selectedBuildingId,
        orgId,
        query,
        page: nextPage
      });
      setSearchResidents((prev) => mergeResidents(prev, list));
      setSearchPage(page);
      setSearchLastPage(lastPage);
    } catch (err) {
      // Keep current list; user can retry Load more.
    } finally {
      setLoadingMoreSearch(false);
    }
  };

  const handleStart = () => {
    if (!canStart) return;
    dispatch(
      createChatDirect({
        body: { resident_user_id: selectedResidentId },
        success: () => {
          onStarted();
        },
        fail: (err) => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: chatErrorMessage(err, t('Failed to start chat')),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  return (
    <Root>
      <ChatPanelHeader title={t('New Chat')} onBack={onBack} onClose={onClose} />

      <Content>
        <Intro>
          <IntroTitle>{t('Start a New Chat')}</IntroTitle>
          <IntroSubtitle>{t('Select a building and then a resident.')}</IntroSubtitle>
        </Intro>

        <SectionLabel>{t('Select Building')}</SectionLabel>
        {hasBuildings ? (
          <ChatBuildingSelect
            value={selectedBuildingId}
            onChange={handleBuildingChange}
            options={buildings}
            ariaLabel={selectedBuildingName}
            variant="filled"
          />
        ) : (
          <ChatEmptyText>{t('No buildings available')}</ChatEmptyText>
        )}

        <SectionLabel>{t('Select Resident')}</SectionLabel>
        <ChatRadioSelect
          value={selectedResidentId}
          selectedLabel={selectedLabel}
          options={buildingResidents}
          getOptionLabel={residentDisplayName}
          placeholder={t('Select Resident')}
          ariaLabel={t('Select Resident')}
          disabled={!selectedBuildingId}
          variant="filled"
          maxMenuHeight={240}
          loading={hasBuildings && loadingBuildingResidents}
          loadingContent={<LoadingAvatar color={LOADING_BLUE} height="12vh" />}
          errorContent={
            !hasBuildings ? (
              <MenuEmpty>{t('Select a building to view residents')}</MenuEmpty>
            ) : buildingResidentsFailed ? (
              <MenuEmpty>{t('Failed to load residents')}</MenuEmpty>
            ) : null
          }
          emptyContent={<MenuEmpty>{t('No residents in this building')}</MenuEmpty>}
          footer={
            hasMoreBuilding ? (
              <LoadMoreButton
                type="button"
                disabled={loadingMoreBuilding}
                onClick={handleLoadMoreBuilding}
              >
                {loadingMoreBuilding ? t('Loading...') : t('Load more')}
              </LoadMoreButton>
            ) : null
          }
          onChange={(_id, user) => handleSelectResident(user)}
        />

        <OrDivider>{t('or')}</OrDivider>

        <SearchSection>
          <GlobalHint>
            {selectedBuildingId
              ? t('Search residents in this building.')
              : t('Select a building first to search residents.')}
          </GlobalHint>
          <ChatSearchField
            variant="filled"
            value={residentSearch}
            onChange={(e) => setResidentSearch(e.target.value)}
            placeholder={t('Search')}
            ariaLabel={t('Search residents in this building.')}
            disabled={!selectedBuildingId}
            onClear={() => {
              setResidentSearch('');
              setDebouncedResidentSearch('');
              setSearchResidents([]);
              setSearchPage(1);
              setSearchLastPage(1);
            }}
          />

          {showingSearchResults && (
            <GlobalResults>
              {loadingSearch && <LoadingAvatar color={LOADING_BLUE} height="12vh" />}
              {!loadingSearch && searchFailed && (
                <MenuEmpty>{t('Failed to load residents')}</MenuEmpty>
              )}
              {!loadingSearch && !searchFailed && searchResidents.length === 0 && (
                <MenuEmpty>{t('No matching residents')}</MenuEmpty>
              )}
              {!loadingSearch &&
                !searchFailed &&
                searchResidents.map((user) => {
                  const selected = Number(selectedResidentId) === Number(user.id);
                  return (
                    <Option
                      key={user.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => handleSelectResident(user)}
                    >
                      <OptionName>{residentDisplayName(user)}</OptionName>
                      <RadioMark $selected={selected} aria-hidden="true">
                        {selected && <IoCheckmark size={12} />}
                      </RadioMark>
                    </Option>
                  );
                })}
              {!loadingSearch && !searchFailed && hasMoreSearch && (
                <LoadMoreButton
                  type="button"
                  disabled={loadingMoreSearch}
                  onClick={handleLoadMoreSearch}
                >
                  {loadingMoreSearch ? t('Loading...') : t('Load more')}
                </LoadMoreButton>
              )}
            </GlobalResults>
          )}
        </SearchSection>

        {createDirectError && <ErrorText>{createDirectError}</ErrorText>}
      </Content>

      <Footer>
        <StartButton type="button" disabled={!canStart} onClick={handleStart}>
          {starting ? t('Starting...') : t('Start Chat')}
        </StartButton>
      </Footer>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IntroTitle = styled.h3`
  margin: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0;
`;

const IntroSubtitle = styled.p`
  margin: 0;
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const SectionLabel = styled.p`
  margin: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;

const OrDivider = styled.p`
  margin: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
`;

const GlobalHint = styled.p`
  margin: 0;
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const GlobalResults = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  background: ${Palette.WHITE};
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${Palette.ERROR};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 13px;
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: 12px 1.25rem 1.25rem;
  border-top: 1px solid ${Palette.SOLITUDE};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: ${Palette.WHITE};
`;

const StartButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  height: 36px;
  padding: 0 24px;
  background: ${Palette.HAZY_BLUE};
  color: ${Palette.WHITE};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    background: ${Palette.DISABLED};
    color: #6e6e6f;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: ${Palette.OCEANIC_NAV_HOVER};
  }

  &:focus {
    outline: none;
  }
`;

export default ChatNewConversation;
